/**
 * Die Zeitfenster des Datenpunkt-Verlaufs (HM-053).
 *
 * **Rollierende Fenster, keine Kalenderzeiträume** — und das ist hier die
 * ehrliche Form, nicht die bequeme. „24 Stunden" heißt die letzten 24 Stunden;
 * es steht kein „heute" auf dem Knopf, weil keines gemeint ist. Ein Kalendertag
 * verlangt die Zeitzone der Anlage und eine Tagesgrenze, die zweimal im Jahr 23
 * oder 25 Stunden lang ist (`@hausmeter/timeseries`); an einer Rohwertreihe, die
 * ein Gerät im Minutentakt liefert, gewänne man damit nichts und verspräche eine
 * Auswertung, die es erst mit den Zählwerksreihen gibt (HM-008, HM-010).
 *
 * Deshalb ist die Rechnung hier auch **keine Zeitzonen-Arithmetik** im Sinne von
 * Regel 3: Es werden Stunden addiert, keine Tage. Wer hier je einen Kalendertag
 * braucht, holt sich `startOfLocalDay` aus `@hausmeter/timeseries` und schreibt
 * ihn nicht nach.
 *
 * **Das Fenster endet bei „jetzt" und wird beim Abruf gebildet**, nicht beim
 * Zeichnen: Sonst verschöbe sich `to` bei jedem Neuzeichnen um Millisekunden,
 * und der Abfrageschlüssel wäre jedes Mal ein anderer — React Query lüde die
 * Reihe endlos nach.
 */
import type { HistoryRange } from "./router.js";

const HOUR_MS = 60 * 60 * 1000;

/** Wie weit jedes Fenster zurückreicht, in Stunden. */
const HOURS: Record<HistoryRange, number> = {
  "24h": 24,
  "7d": 7 * 24,
  "30d": 30 * 24,
};

/** Die Beschriftung der Knöpfe — und dieselben Worte im Fließtext. */
export const RANGE_LABELS: Record<HistoryRange, string> = {
  "24h": "24 Stunden",
  "7d": "7 Tage",
  "30d": "30 Tage",
};

export interface TimeWindow {
  readonly from: string;
  readonly to: string;
}

/**
 * Das Fenster zu einem Zeitraum.
 *
 * **Auf die volle Minute abgerundet.** Ohne das trüge jeder Abruf einen anderen
 * `from`-Wert, und zwei Ansichten desselben Verlaufs — etwa nach einem Wechsel
 * des Reiters — teilten sich keinen zwischengespeicherten Stand mehr. Eine
 * Minute Unschärfe an einem 24-Stunden-Fenster ist keine.
 */
export function rangeWindow(range: HistoryRange, now: number = Date.now()): TimeWindow {
  const to = Math.floor(now / 60_000) * 60_000;
  return {
    from: new Date(to - HOURS[range] * HOUR_MS).toISOString(),
    to: new Date(to).toISOString(),
  };
}

/**
 * Ob die Aufbewahrung dieses Datenpunkts in das Fenster hineinreicht.
 *
 * **Der Unterschied zwischen einer Lücke und einer gelöschten Zeit** (Regel 8):
 * Vor `retainedFrom` fehlen die Werte nicht, weil das Gerät geschwiegen hätte,
 * sondern weil sie planmäßig gelöscht sind (HM-048). Ohne diesen Hinweis läse
 * man die leere linke Hälfte des Diagramms als tagelangen Ausfall.
 */
export function retentionCutsInto(window: TimeWindow, retainedFrom: string | null): boolean {
  if (retainedFrom === null) return false;
  return new Date(retainedFrom).getTime() > new Date(window.from).getTime();
}
